/**
 * Converts markdown text to plain text with the following transformations:
 * - Removes markdown formatting (bold, italic, strikethrough, code, etc.)
 * - Removes or simplifies markdown links
 * - Truncates long URLs
 * - Removes list markers and blockquotes
 * - Truncates to maxChars if needed
 */
export function getShortTextFromMarkdown(markdown: string, maxChars = 200): string {
    let plainText = markdown
        .replace(/!\[.*?\]\(.*?\)/g, '') // Remove images
        // Handle markdown links: keep text if it's meaningful, otherwise just keep the URL
        .replace(/\[([^\]]*)\]\(([^)]*)\)/g, (match, text, url) => {
            // If text is empty, just a URL, or very short, just return the URL (will be abbreviated later)
            if (
                !text ||
                text.startsWith('http://') ||
                text.startsWith('https://') ||
                text.length < 3
            ) {
                return url
            }
            // Otherwise keep the meaningful text
            return text
        })
        .replace(/\[|\]/g, '') // Remove any remaining standalone brackets
        .replace(/\*\*\*(.*?)\*\*\*/g, '$1') // Remove bold+italic (*** or ***)
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
        .replace(/__(.*?)__/g, '$1') // Remove bold (alternative syntax)
        .replace(/\*(.*?)\*/g, '$1') // Remove italics
        .replace(/_(.*?)_/g, '$1') // Remove italics (alternative syntax)
        .replace(/~~(.*?)~~/g, '$1') // Remove strikethrough
        .replace(/`{1,3}(.*?)`{1,3}/g, '$1') // Remove inline code
        .replace(/^[>#\-*+]\s+/gm, '') // Remove list markers and blockquotes

    // Truncate long URLs
    plainText = plainText.replace(/https?:\/\/[^\s]+/g, (url) => {
        if (url.length > 30) {
            // Show first part of domain + "..."
            const urlMatch = url.match(/^(https?:\/\/[^/]+)/)
            const domain = urlMatch ? urlMatch[1] : url.substring(0, 20)
            return domain.length > 25 ? domain.substring(0, 25) + '...' : domain + '...'
        }
        return url
    })

    if (maxChars <= 0) {
        return ''
    }

    if (
        plainText.length > maxChars &&
        plainText[maxChars - 1].match(/\S/) &&
        plainText[maxChars]?.match(/\S/)
    ) {
        // Find the last space before the maxChars character to avoid cutting off a word
        const uptoLastWord = plainText.substring(0, maxChars).lastIndexOf(' ')
        // If no space found (e.g., long URL or single word), truncate at char boundary
        if (uptoLastWord === -1) {
            return plainText.substring(0, maxChars)
        }
        return plainText.substring(0, uptoLastWord).trim() + '...'
    }

    return plainText.substring(0, maxChars).trim()
}
